import {
    // eslint-disable-next-line no-restricted-imports
    Modal as ChakraModal,
    ModalProps as ChakraModalProps,
    Divider,
    ModalBody,
    ModalBodyProps,
    ModalCloseButton,
    ModalContent,
    ModalContentProps,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Button, ButtonProps, IconButton } from '../Button';
import { useGlobalTrapFocus } from '../GlobalTrapFocusContext';
import { ChevronLeft } from '../icons';
import { scrollShadowStyle } from '../styleHelpers';
import { Header } from '../Typography';

export type ModalProps = Omit<ChakraModalProps, 'children'> & {
    title?: string | JSX.Element;
    renderHeader?: (onClose?: () => void) => JSX.Element;
    renderFooter?: (onClose?: () => void) => JSX.Element;
    renderContent?: (onClose?: () => void) => JSX.Element;
    unstyled?: boolean;
    showDefaultFooter?: boolean;
    onBack?: VoidFunction;
    children?: JSX.Element;
    slideDown?: boolean;
    showCloseButton?: boolean;
    modalContentProps?: ModalContentProps;
    modalBodyProps?: ModalBodyProps;
    maxH?: string | number;
    isFullScreen?: boolean;
};

const Modal = ({
    isOpen,
    onClose,
    onBack,
    title,
    renderHeader,
    renderFooter,
    renderContent,
    scrollBehavior = 'inside',
    unstyled,
    showDefaultFooter,
    children,
    modalContentProps,
    modalBodyProps,
    slideDown,
    showCloseButton = true,
    isFullScreen,
    ...modalProps
}: ModalProps) => {
    const { trapFocus } = useGlobalTrapFocus();

    // Only used when slideDown is set
    const [marginTop, setMarginTop] = useState(-1000);

    useEffect(() => {
        if (slideDown && isOpen) {
            setTimeout(() => {
                setMarginTop(10);
            }, 100);
        }
    }, [slideDown, isOpen]);

    if (!isOpen) return null;

    if (slideDown) {
        modalContentProps = {
            ...modalContentProps,
            ...{
                marginTop,
                as: motion.div,
                layout: true,
            },
        };
    }

    if (isFullScreen) {
        modalContentProps = {
            ...modalContentProps,
            my: 8,
            maxH: '95vh',
        };
    }

    if (unstyled) {
        return (
            <AnimatePresence>
                <ChakraModal
                    isOpen={isOpen}
                    onClose={onClose}
                    scrollBehavior={scrollBehavior}
                    trapFocus={trapFocus}
                    {...modalProps}
                >
                    <ModalOverlay />
                    <ModalContent {...modalContentProps}>
                        {children}
                    </ModalContent>
                </ChakraModal>
            </AnimatePresence>
        );
    }

    const headerContent = title
        ? title
        : renderHeader
          ? renderHeader(onClose)
          : undefined;

    const backButton = onBack ? (
        <ModalBackButton
            onClick={onBack}
            onClickEvent="Modal Back Button Clicked"
            onClickEventParams={{ title }}
        />
    ) : undefined;

    return (
        <AnimatePresence>
            <ChakraModal
                isOpen={isOpen}
                onClose={onClose}
                scrollBehavior={scrollBehavior}
                trapFocus={trapFocus}
                {...modalProps}
            >
                <ModalOverlay />
                <ModalContent {...modalContentProps}>
                    {headerContent && (
                        <ModalHeader>
                            {backButton}
                            <Header>{headerContent}</Header>
                            <Divider mt={6} px={10} mx={-10} />
                        </ModalHeader>
                    )}
                    {showCloseButton && (
                        <ModalCloseButton
                            tabIndex={0}
                            size="lg"
                            color="textMuted"
                            zIndex={2}
                        />
                    )}
                    <ModalBody
                        position="relative"
                        overflow="auto"
                        {...scrollShadowStyle()}
                        {...modalBodyProps}
                    >
                        {!headerContent && backButton}
                        {renderContent ? renderContent(onClose) : children}
                    </ModalBody>
                    {renderFooter && renderFooter(onClose)}
                    {showDefaultFooter && (
                        <ModalFooter>
                            <Button
                                variant="ghost"
                                colorScheme="blue"
                                mr={3}
                                onClick={onClose}
                                onClickEvent="Modal Close Button Clicked"
                                onClickEventParams={{ title }}
                            >
                                Close
                            </Button>
                        </ModalFooter>
                    )}
                </ModalContent>
            </ChakraModal>
        </AnimatePresence>
    );
};

function ModalBackButton(props: ButtonProps) {
    return (
        <IconButton
            {...props}
            aria-label="back"
            icon={<ChevronLeft />}
            size="sm"
            colorScheme="gray"
            variant="ghost"
            position="absolute"
            top={2}
            left={3}
            w={8}
            h={8}
            minW="auto"
        />
    );
}

export default Modal;
